import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 74. NE PAS METTRE TOUT DANS CORE
// Zifek Core doit rester STABLE. Il fournit uniquement les FONDATIONS
// communes (users, businesses, customers, contacts, employees, products,
// services, documents, calendar, locations, permissions, notifications,
// events, workflows, files, reviews, settings, api, audit, search).
//
// Ne vivent JAMAIS dans le Core : comptabilité complète, paie, stock avancé,
// PMS hôtelier, CRM avancé, fabrication, campagnes marketing complexes.
// Ces domaines restent des MODULES, branchés sur les fondations du Core.
// ─────────────────────────────────────────────────────────────

export type BoundaryOwnership = 'core' | 'module';

export interface CoreBoundary {
  id: number;
  capability: string;
  area: string | null;
  ownership: BoundaryOwnership;
  module_key: string | null;
  description: string | null;
  rationale: string | null;
  is_core_foundation: boolean | null;
  sort_order: number | null;
}

/** Principe fondateur : le Core reste la fondation stable et commune. */
export const CORE_STABILITY_PRINCIPLE =
  'Le Core reste stable : il ne contient que les fondations communes. Tout ce qui est métier et évolutif vit dans un module, branché sur ces fondations.';

function mapBoundary(row: Record<string, unknown>): CoreBoundary {
  return {
    id: Number(row.id),
    capability: String(row.capability ?? ''),
    area: (row.area as string) ?? null,
    ownership: ((row.ownership as string) === 'core' ? 'core' : 'module') as BoundaryOwnership,
    module_key: (row.module_key as string) ?? null,
    description: (row.description as string) ?? null,
    rationale: (row.rationale as string) ?? null,
    is_core_foundation: row.is_core_foundation != null ? Boolean(row.is_core_foundation) : null,
    sort_order: row.sort_order != null ? Number(row.sort_order) : null,
  };
}

export async function listBoundaries(): Promise<CoreBoundary[]> {
  const { data, error } = await supabase
    .from('core_boundaries')
    .select('*')
    .order('ownership', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => mapBoundary(r as Record<string, unknown>));
}

export async function listCoreFoundations(): Promise<CoreBoundary[]> {
  const all = await listBoundaries();
  return all.filter((b) => b.ownership === 'core');
}

export async function listModuleOwnedDomains(): Promise<CoreBoundary[]> {
  const all = await listBoundaries();
  return all.filter((b) => b.ownership === 'module');
}

/** Retrouve la frontière d'une capacité (par clé). */
export async function classifyCapability(capability: string): Promise<CoreBoundary | null> {
  const key = capability.trim().toLowerCase();
  const { data } = await supabase.from('core_boundaries').select('*').eq('capability', key).maybeSingle();
  return data ? mapBoundary(data as Record<string, unknown>) : null;
}

export interface ScopeDecision {
  ok: boolean;
  ownership: BoundaryOwnership | 'unknown';
  capability: string;
  moduleKey: string | null;
  reason: string;
}

/**
 * Garde-fou : vérifie qu'une capacité a le droit de vivre dans le Core.
 * Utilisé au moment d'enregistrer/déclarer un module : une capacité
 * « module-owned » (paie, PMS…) est REFUSÉE dans le périmètre Core.
 */
export async function assertInCoreScope(capability: string): Promise<ScopeDecision> {
  const boundary = await classifyCapability(capability);
  if (!boundary) {
    return {
      ok: true,
      ownership: 'unknown',
      capability,
      moduleKey: null,
      reason: 'Capacité inconnue : considérée comme extensible (module), pas comme une fondation du Core.',
    };
  }
  if (boundary.ownership === 'core') {
    return {
      ok: true,
      ownership: 'core',
      capability,
      moduleKey: boundary.module_key,
      reason: 'Capacité commune : a sa place dans le Core.',
    };
  }
  return {
    ok: false,
    ownership: 'module',
    capability,
    moduleKey: boundary.module_key,
    reason: `${boundary.capability} ne doit pas vivre dans le Core : ${
      boundary.rationale || 'domaine métier réservé à un module.'
    }`,
  };
}

export interface BoundaryStats {
  total: number;
  coreFoundations: number;
  moduleDomains: number;
  areas: number;
}

export async function fetchBoundaryStats(): Promise<BoundaryStats> {
  const all = await listBoundaries();
  return {
    total: all.length,
    coreFoundations: all.filter((b) => b.ownership === 'core').length,
    moduleDomains: all.filter((b) => b.ownership === 'module').length,
    areas: new Set(all.map((b) => b.area || 'Autre')).size,
  };
}

export async function createBoundary(input: {
  capability: string;
  area?: string | null;
  ownership: BoundaryOwnership;
  moduleKey?: string | null;
  description?: string | null;
  rationale?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('core_boundaries').insert({
    capability: input.capability.trim().toLowerCase(),
    area: input.area || null,
    ownership: input.ownership,
    module_key: input.moduleKey || null,
    description: input.description || null,
    rationale: input.rationale || null,
    is_core_foundation: input.ownership === 'core',
    sort_order: 90,
  });
  if (error) throw error;
}

export async function deleteBoundary(id: number): Promise<void> {
  const { error } = await supabase.from('core_boundaries').delete().eq('id', id);
  if (error) throw error;
}